import { Component, DestroyRef, inject, Input, OnChanges, signal, SimpleChanges } from '@angular/core';

import { TranslateModule } from '@ngx-translate/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { AiKnowledgeService } from '../../../services/ai-knowledge.service';
import {
  KnowledgeSource,
  KnowledgeSourceConfig,
  KnowledgeSourceConfigWebsiteScrapeConfig,
  KnowledgeSourceConfigWebsiteScrapeConfigScrapedPage,
  KnowledgeSourceTraining,
  TrainingProgress,
} from '@vendasta/embeddings';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { map } from 'rxjs';
import { AiKnowledgeI18nModule } from '../../../assets/i18n/ai-knowledge-i18n.module';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { TrainingProgressComponent } from '../../../shared/training-progress/training-progress.component';
import {
  TrainingProgressConfig,
  shouldShowTrainingProgress,
} from '../../../shared/training-progress/training-progress.utils';

export const STEP_OPTION = 'scrapedPages';

@Component({
  selector: 'ai-knowledge-scraped-pages',
  imports: [
    TranslateModule,
    AiKnowledgeI18nModule,
    MatCheckboxModule,
    ReactiveFormsModule,
    FormsModule,
    GalaxyFormFieldModule,
    GalaxyEmptyStateModule,
    GalaxyLoadingSpinnerModule,
    MatExpansionModule,
    MatIcon,
    TrainingProgressComponent
],
  providers: [AiKnowledgeService],
  templateUrl: './scraped-pages.component.html',
  styleUrls: ['./scraped-pages.component.scss'],
})
export class ScrapedPagesComponent implements OnChanges {
  @Input() knowledgeSource!: KnowledgeSource;
  @Input() shouldShowDone = false;
  private readonly knowledgeService = inject(AiKnowledgeService);
  destroyRef = inject(DestroyRef);
  form!: FormGroup;
  websiteScrapeConfig: KnowledgeSourceConfigWebsiteScrapeConfig | undefined;
  trainingInfo: KnowledgeSourceTraining | null = null;
  readonly isLoading = signal(true);
  delayProgressScreenClosure = false;

  get trainingProgressConfig(): TrainingProgressConfig {
    return {
      trainingInfo: this.trainingInfo,
      delayProgressScreenClosure: this.delayProgressScreenClosure,
      trainingEnabledPageCount: this.getTrainingEnabledPageCount(),
    };
  }

  shouldShowTrainingProgress(): boolean {
    return shouldShowTrainingProgress(this.trainingProgressConfig, this.shouldShowDone);
  }

  private getTrainingEnabledPageCount(): number {
    if (!this.websiteScrapeConfig?.scrapedPages) {
      return 0;
    }

    let count = 0;
    for (const page of this.websiteScrapeConfig.scrapedPages) {
      if (page.trainingEnabled) {
        count++;
      }
    }
    return count;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.knowledgeSource) {
      this.websiteScrapeConfig = this.knowledgeSource?.config?.websiteScrapeConfig;
      this.trainingInfo = this.knowledgeSource?.training ?? null;

      if (!this.knowledgeSource || !this.websiteScrapeConfig?.scrapedPages) return;

      this.websiteScrapeConfig?.scrapedPages?.sort((a, b) =>
        a?.trainingEnabled === b?.trainingEnabled ? a?.url?.localeCompare(b?.url) : a?.trainingEnabled ? -1 : 1,
      );

      this.form = new FormGroup({
        scrapedPages: new FormArray([]),
      });
      const controls = this.websiteScrapeConfig?.scrapedPages?.map((page) => new FormControl(page.trainingEnabled));
      this.form.controls.scrapedPages = new FormArray(controls);
      this.form.controls.scrapedPages.valueChanges
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          map(() => this.form.markAsDirty()),
        )
        .subscribe();

      this.isLoading.set(false);
    }
  }

  get scrapedPagesArray(): FormArray<FormControl> {
    return this.form.controls['scrapedPages'] as FormArray<FormControl>;
  }

  setAllScrapedPages(value: boolean): void {
    this.form.controls.scrapedPages = new FormArray(
      this.websiteScrapeConfig?.scrapedPages?.map((p) => {
        const training = p?.errorMessage ? false : value;
        return new FormControl(training);
      }) ?? [],
    );
    this.form.markAsDirty();
  }

  getSuccessfullyScrapedPagesCount(): number {
    return this.websiteScrapeConfig?.scrapedPages?.filter((p) => !p?.errorMessage).length || 0;
  }

  getErroredScrapedPages(): KnowledgeSourceConfigWebsiteScrapeConfigScrapedPage[] {
    return this.websiteScrapeConfig?.scrapedPages?.filter((p) => p?.errorMessage) ?? [];
  }

  getErroredScrapedPagesCount(): number {
    return this.getErroredScrapedPages().length || 0;
  }

  hasUnsavedChanges(): boolean {
    return this.form?.dirty;
  }

  async submit(): Promise<string> {
    this.scrapedPagesArray.controls.forEach((pageControl, index) => {
      const page = this.websiteScrapeConfig?.scrapedPages?.[index];
      if (page) {
        page.trainingEnabled = pageControl.value;
      }
    });
    const config = {
      websiteScrapeConfig: this.websiteScrapeConfig,
    } as KnowledgeSourceConfig;
    if (this.knowledgeSource) {
      const source = this.knowledgeSource;
      source.config = config;
      await this.knowledgeService.updateKnowledgeSourceNameAndConfig(source);
      this.form.markAsPristine();
      return source.id;
    }
    throw new Error('No knowledge source');
  }

  protected readonly TrainingProgress = TrainingProgress;
}
